import React, { useRef, useEffect, useState } from "react";
import { Event } from "../../types";
import styles from "./EventsList.module.css";
import { formatEventTime } from "../../utils/timeUtils";
import { useAppSelector } from "../../hooks/redux";
import { LocationPlaceholder } from "../LocationPlaceholder/LocationPlaceholder";
import { Webcam } from "../../types";
interface EventsListProps {
  events: Event[];
  selectedType?: string;
  webcams?: Webcam[];
  onLocationClarificationRequest?: (data: {
    event: Event;
    title: string;
    coordinates: [number, number];
    initialCoordinates?: [number, number];
  }) => void;
  onWebcamSelect?: (webcam: Webcam) => void;
}

// Утилиты для работы с локациями
const getLocationInfo = (event: Event) => {
  if (!event.locations || event.locations.length === 0) {
    return { hasLocation: false };
  }

  const location = event.locations[0];

  if (!location.points || location.points.length === 0) {
    return { hasLocation: false };
  }

  const point = location.points[0];

  if (
    !point.latitude ||
    !point.longitude ||
    point.latitude === 0 ||
    point.longitude === 0 ||
    isNaN(point.latitude) ||
    isNaN(point.longitude)
  ) {
    return { hasLocation: false };
  }

  return {
    hasLocation: true,
    coordinates: [point.longitude, point.latitude] as [number, number],
    locationName: location.name || event.location_name || "",
  };
};

export const EventsList: React.FC<EventsListProps> = ({
  events,
  selectedType,
  webcams = [],
  onWebcamSelect,
  onLocationClarificationRequest,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  const { selectedCity } = useAppSelector((state) => state.map);
  // Фильтруем события по выбранному типу

  const filteredEvents = events.filter((event) => {
    if (!selectedType) return true;

    if (selectedType === "webcams") return false;

    if (selectedType === "dps" && event.type === 1) return true;
    if (selectedType === "clean" && event.type === 2) return true;
    if (selectedType === "accident" && event.type === 3) return true;

    return false;
  });
  const { userPremium } = useAppSelector((state) => state.map);

  const renderEventMessage = (event: Event) => {
    if (event.replies && event.replies.length > 0) {
      const sortedReplies = [...event.replies].sort(
        (a, b) => a.Level - b.Level
      );

      return (
        <div className={styles.eventMessage}>
          {sortedReplies.map((reply) => (
            <React.Fragment key={reply.ID}>
              <i>{reply.Message} → </i>
            </React.Fragment>
          ))}
          {event.message}
        </div>
      );
    }

    return <div className={styles.eventMessage}>{event.message}</div>;
  };
  // Проверяем нужен ли скролл
  useEffect(() => {
    if (containerRef.current && filteredEvents.length > 0) {
      const container = containerRef.current;
      const needsScrolling = container.scrollHeight > container.clientHeight;
      setNeedsScroll(needsScrolling);
    } else {
      setNeedsScroll(false);
    }
  }, [filteredEvents]);

  // Функция для получения текста пустого состояния
  const getEmptyStateText = () => {
    // Для всех типов показываем одинаковое сообщение если нет премиума
    if (!userPremium) {
      return {
        title: "",
        subtitle:
          "Следи за ситуацией на дороге: получай свежие сводки о постах, авариях и прочих событиях с подпиской ДПС Premium",
      };
    }

    // Если есть премиум - показываем оригинальные сообщения
    switch (selectedType) {
      case "dps":
        return {
          title: "Патрули не замечены",
          subtitle: "За последние 2 часа информация о ДПС не поступала",
        };
      case "clean":
        return {
          title: "Обстановка без изменений",
          subtitle: "Пока нет новых сообщений",
        };
      case "accident":
        return {
          title: "Все спокойно",
          subtitle: "За последние 2 часа аварий не зафиксировано",
        };
      default:
        return {
          title: "Нет событий",
          subtitle: "Пока нет новых сообщений",
        };
    }
  };
  if (selectedType === "webcams") {
    if (webcams.length === 0) {
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyTitle}>Камеры недоступны</h3>
          <p className={styles.emptySubtitle}>
            В данном городе нет доступных веб-камер
          </p>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        {webcams.map((webcam, index) => (
          <div key={webcam.id}>
            <div className={styles.webcamItem}>
              <button
                className={styles.webcamButton}
                onClick={() => onWebcamSelect?.(webcam)}
              >
                <span className={styles.webcamName}>{webcam.name}</span>
                <svg
                  className={styles.arrowIcon}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            {index < webcams.length - 1 && <div className={styles.separator} />}
          </div>
        ))}
      </div>
    );
  }
  const handleLocationClarification = (event: Event) => {
    if (!onLocationClarificationRequest || !selectedCity) return;

    // Используем координаты центра города
    const cityCoordinates: [number, number] = [
      selectedCity.longitude,
      selectedCity.latitude,
    ];

    onLocationClarificationRequest({
      event,
      title: event.message || "Событие требует уточнения локации",
      coordinates: cityCoordinates,
      // НЕ передаем initialCoordinates - это не уточнение, а добавление новой локации
    });
  };
  const renderLocationInfo = (event: Event) => {
    const locationInfo = getLocationInfo(event);

    if (!locationInfo.hasLocation) {
      return (
        <LocationPlaceholder
          onClick={() => handleLocationClarification(event)}
        />
      );
    }

    return null;
  };

  if (filteredEvents.length === 0) {
    const emptyState = getEmptyStateText();
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyTitle}>{emptyState.title}</h3>
        <p className={styles.emptySubtitle}>{emptyState.subtitle}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${needsScroll ? styles.scrollable : ""}`}
      style={{
        overflowY: needsScroll ? "auto" : "hidden",
      }}
    >
      {filteredEvents.map((event, index) => (
        <div key={`${event.timestamp}-${index}`}>
          <div className={styles.eventItem}>
            <div className={styles.eventContent}>
              {renderEventMessage(event)}
              <div className={styles.eventMeta}>
                <span className={styles.eventTime}>
                  {formatEventTime(event.timestamp)}
                </span>
                <span className={styles.dotSeparator}>•</span>
                <span className={styles.eventUser}>{event.from}</span>
              </div>
              {renderLocationInfo(event)}
            </div>
          </div>
          {index < filteredEvents.length - 1 && (
            <div className={styles.separator} />
          )}
        </div>
      ))}
    </div>
  );
};
